import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components/macro";

import {
  Grid,
  Card as MuiCard,
  Divider as MuiDivider,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";

import moment from "moment";
import { toast } from "react-toastify";
import {
  createAmaliat,
  getAllAmaliatCategoryWOP,
  getAllAmaliatLabelWOP,
  getAmaliatById,
  getAmaliatForUpdate,
  updateAmaliat,
} from "../../../api/amaliat";
import { uploadDbFile } from "../../../api/dbFile";
import { getAllLanguage } from "../../../api/language";
import AmaliatForm from "../../../components/form/amaliat/AmaliatForm";
import CustomBreadcrumbs from "../../../components/ui/CustomBreadcrumbs";
import params from "../../../utils/params";

const Card = styled(MuiCard)(spacing);

const Divider = styled(MuiDivider)(spacing);

const AddAmaliat = () => {
  // states
  const [amaliat, setAmaliat] = useState({});
  const [amaliatData, setAmaliatData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmittingAmaliat, setIsSubmittingAmaliat] = useState(false);
  const [allAmaliatLabel, setAllAmaliatLabel] = useState([]);
  const [allAmaliatCategory, setAllAmaliatCategory] = useState([]);
  const [allLanguage, setAllLanguage] = useState([]);

  // Radio button control
  const [radioValue, setRadioValue] = React.useState("3");

  const handleRadioChange = (event) => {
    setRadioValue(event.target.value);
  };

  // objects
  const navigate = useNavigate();
  const paramUrl = useParams();
  const amaliatId = paramUrl?.id;

  const breadcrumbsList = [
    {
      name: "Dashboard",
      url: "/",
    },
    {
      name: "Amaliyat List",
      url: "/amaliat",
    },
    {
      name: amaliatId ? "Update" : "Add",
      url: "#",
    },
  ];

  // functions
  const fetchAmaliatFn = React.useCallback(async () => {
    if (amaliatId) {
      try {
        let response = await getAmaliatById(amaliatId);
        setAmaliat(response.data.payload);
      } catch (error) {
        console.log("Error: ", error);
      }
    }
  }, [amaliatId]);

  const fetchAmaliatDataFn = React.useCallback(async () => {
    if (amaliatId) {
      try {
        let params = {
          loadAudioDbFile: true,
        };
        let response = await getAmaliatForUpdate(amaliatId, { params });
        let responsePayload = response?.data?.payload;

        setAmaliatData(response.data.payload);
        if (responsePayload?.amaliyatVisibilityDayMonths.length) {
          setRadioValue("2");
        } else if (
          responsePayload.hasOwnProperty("startVisibilityDayMonth") &&
          responsePayload["startVisibilityDayMonth"] &&
          responsePayload.hasOwnProperty("endVisibilityDayMonth") &&
          responsePayload["endVisibilityDayMonth"]
        ) {
          setRadioValue("3");
        } else {
          setRadioValue("1");
        }
      } catch (error) {
        console.log("Error: ", error);
      }
    }
  }, [amaliatId]);

  const fetchAllLanguageFn = async () => {
    try {
      let response = await getAllLanguage();
      setAllLanguage(response.data.payload);
    } catch (error) {
      console.log("Get All Language Error: ", error);
    }
  };

  const fetchAllAmaliatLabelWOP = async () => {
    let response = await getAllAmaliatLabelWOP();
    setAllAmaliatLabel(response?.data?.payload);
  };

  const fetchAllAmaliatCategoryWOP = async () => {
    try {
      const response = await getAllAmaliatCategoryWOP(
        params({
          sortBy: "creationDate",
          ascOrDesc: "desc",
        })
      );
      const data = response?.data?.payload;

      setAllAmaliatCategory(data);
    } catch (error) {
      console.log("fetch amaliat category error: ", error);
    }
  };

  // submit amaliat
  const submitAmaliat = async (values) => {
    setIsSubmittingAmaliat(true);
    setIsLoading(true);

    // helper to format a day‐month picker value or return null
    const formatDayMonth = (pickerValue) => {
      if (!pickerValue) return null;
      const dateObj = pickerValue.$d ?? pickerValue._d ?? pickerValue;
      return dateObj ? moment(dateObj).format("DD-MM") : null;
    };

    try {
      // 1. Basic validation
      if (!values.amaliyatLabelId?.id || !values.amaliyatCategoryId?.id) {
        throw new Error("Please select both label and category.");
      }

      // 2. Compute visibility dates
      const start =
        radioValue !== "2"
          ? formatDayMonth(values.startVisibilityDayMonth)
          : null;
      const end =
        radioValue === "3"
          ? formatDayMonth(values.endVisibilityDayMonth)
          : null;

      // 3. Filter out empty subtitles
      const subTitles =
        values.subTitles?.filter((st) => st.text?.trim().length > 0) || [];

      // 4. Prepare the base payload
      const payload = {
        id: amaliatId ? parseInt(amaliatId, 10) : undefined,
        amaliyatLabelId: values.amaliyatLabelId.id,
        amaliyatCategoryId: values.amaliyatCategoryId.id,
        active: values.active ?? true,
        recommended: values.recommended ?? true,
        titles: values.titles || [],
        subTitles,
        namazTracking: values.namazTracking ?? false,
        startVisibilityDayMonth: start,
        endVisibilityDayMonth: end,
        amaliyatVisibilityDayMonths:
          radioValue === "2"
            ? values.amaliyatVisibilityDayMonths || []
            : [],
      };

      // 5. Handle file upload if needed
      let audioDbFileId = 0;
      if (values.file && !values.file.fileUrl) {
        console.log("🚀 ~ submitAmaliat ~ values.file:", values.file);
        const formData = new FormData();
        formData.append("file", values.file);
        formData.append("fileType", values.file.type);
        formData.append("fileUploadType", "AMALIYAT");

        const uploadRes = await uploadDbFile(formData);
        if (!uploadRes?.data?.success) {
          throw new Error(
            uploadRes?.data?.message || "Audio upload failed."
          );
        }
        audioDbFileId = uploadRes.data.payload.id;
      } else if (values.file?.fileUrl) {
        audioDbFileId = values.file.id;
      }

      // 6. Merge file ID into payload
      payload.audioDbFileId = audioDbFileId;

      // 7. Create or update
      let response;
      if (amaliatId) {
        response = await updateAmaliat(payload);
        if (!response.data.success) {
          throw new Error(response.data.message || "Update failed.");
        }
        toast.success("Amaliyat updated successfully.");
      } else {
        response = await createAmaliat(payload);
        if (!response.data.success) {
          throw new Error(response.data.message || "Creation failed.");
        }
        toast.success("Amaliyat created successfully.");
      }

      // 8. Navigate back
      navigate("/amaliat");
    } catch (err) {
      // Extract message (could be from Axios error or our throw)
      const msg =
        err.response?.data?.message || err.message || "Something went wrong.";
      setErrorMessage(msg);
    } finally {
      setIsSubmittingAmaliat(false);
      setIsLoading(false);
    }
  };


  useEffect(() => {
    fetchAmaliatFn();
    fetchAmaliatDataFn();
    fetchAllAmaliatLabelWOP();
    fetchAllAmaliatCategoryWOP();
    fetchAllLanguageFn();
  }, [fetchAmaliatDataFn, fetchAmaliatFn]);

  return (
    <React.Fragment>
      <Helmet title={amaliatId ? "Update Amaliyat" : "Add Amaliyat"} />
      <Typography variant="h3" gutterBottom display="inline">
        {amaliatId ? "Update Amaliyat" : "Add Amaliyat"}
      </Typography>

      <CustomBreadcrumbs breadcrumbsList={breadcrumbsList} />

      <Divider my={3} />

      <Grid container spacing={6} justifyContent={"center"}>
        <Grid item xs={12} lg={10} xl={6}>
          <Card p={8}>
            <AmaliatForm
              isSubmittingAmaliat={isSubmittingAmaliat}
              isLoading={isLoading}
              errorMessage={errorMessage}
              navigate={navigate}
              amaliat={amaliat}
              allAmaliatCategory={allAmaliatCategory}
              allAmaliatLabel={allAmaliatLabel}
              onSubmit={submitAmaliat}
              allLanguage={allLanguage}
              amaliatData={amaliatData}
              //
              radioValue={radioValue}
              handleRadioChange={handleRadioChange}
            />
          </Card>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default AddAmaliat;
